import { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Users2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const FREQ_OPTIONS = [
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quincenal" },
  { value: "monthly", label: "Mensual" },
  { value: "hourly", label: "Por hora" },
  { value: "commission", label: "Comisión" },
  { value: "mixed", label: "Mixto" },
];

const empty = () => ({
  first_name: "", last_name: "", email: "", phone: "", birth_date: "",
  rfc: "", curp: "", nss: "",
  position: "", department: "", hire_date: "", employment_type: "full_time",
  frequency: "monthly", gross_salary: "",
  bank: "", clabe: "", account_number: "",
});

export default function EmployeeOnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });
  const canNext =
    (step === 1 && form.first_name && form.last_name) ||
    (step === 2) ||
    (step === 3 && Number(form.gross_salary) > 0) ||
    (step === 4);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form, gross_salary: Number(form.gross_salary || 0) };
      const r = await api.post("/payroll/employees", payload, { withCredentials: true });
      navigate(`/nomina/${r.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar al empleado.");
    } finally { setSaving(false); }
  };

  const stepInfo = [
    { title: "Datos personales", subtitle: "Empecemos con lo básico." },
    { title: "Datos fiscales", subtitle: "RFC, CURP y NSS (podemos completarlos después)." },
    { title: "Puesto y sueldo", subtitle: "¿Cómo y cuánto le pagas?" },
    { title: "Datos bancarios", subtitle: "Para depositarle su sueldo (opcional)." },
  ];

  const current = stepInfo[step - 1];

  return (
    <AppShell>
      <main className="page projects-page" data-testid="employee-onboarding-page">
        <header className="expenses-head">
          <div>
            <button type="button" onClick={() => navigate("/nomina")} className="cash-btn ghost" style={{ marginBottom: 8 }}><ArrowLeft size={14} /> Nómina</button>
            <small className="eyebrow">NUEVO EMPLEADO · PASO {step} DE 4</small>
            <h1>{current.title}</h1>
            <p className="academia-subtitle">{current.subtitle}</p>
          </div>
        </header>

        <div className="tour-progress-bar" style={{ marginBottom: 22 }}><div style={{ width: `${(step / 4) * 100}%` }} /></div>

        <section style={{ background: "#fff", borderRadius: 22, padding: 28, boxShadow: "0 8px 30px rgba(15,23,42,0.06)", maxWidth: 720 }} data-testid={`step-${step}`}>
          {step === 1 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <label>Nombre(s) *<input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="emp-first-name" /></label>
                <label>Apellidos *<input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="emp-last-name" /></label>
              </div>
              <label>Correo electrónico<input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="emp-email" /></label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <label>Teléfono<input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="emp-phone" /></label>
                <label>Fecha de nacimiento<input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="emp-birth" /></label>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <label>RFC (13 caracteres)<input value={form.rfc} onChange={(e) => set("rfc", e.target.value.toUpperCase())} maxLength={13} placeholder="XAXX010101000" data-testid="emp-rfc" /></label>
              <label>CURP (18 caracteres)<input value={form.curp} onChange={(e) => set("curp", e.target.value.toUpperCase())} maxLength={18} data-testid="emp-curp" /></label>
              <label>NSS (11 dígitos)<input value={form.nss} onChange={(e) => set("nss", e.target.value.replace(/\D/g, ""))} maxLength={11} data-testid="emp-nss" /></label>
              <p style={{ background: "#eef4ff", padding: 14, borderRadius: 12, color: "#4169e1", fontSize: 13, marginTop: 12 }}>
                💡 Puedes dejar estos campos vacíos por ahora — solo se marcarán como "faltan datos" hasta que los completes.
              </p>
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <label>Puesto<input value={form.position} onChange={(e) => set("position", e.target.value)} placeholder="Ej: Diseñador" data-testid="emp-position" /></label>
                <label>Departamento<input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="Ej: Ventas" data-testid="emp-department" /></label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <label>Fecha de ingreso<input type="date" value={form.hire_date} onChange={(e) => set("hire_date", e.target.value)} data-testid="emp-hire-date" /></label>
                <label>Frecuencia de pago
                  <select value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="emp-frequency">
                    {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              </div>
              <label>Sueldo bruto por periodo (MXN) *<input type="number" step="0.01" value={form.gross_salary} onChange={(e) => set("gross_salary", e.target.value)} placeholder="10000" data-testid="emp-salary" /></label>
              <p style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>Este es el sueldo bruto por cada periodo (semanal, quincenal o mensual según elegiste).</p>
            </>
          )}

          {step === 4 && (
            <>
              <label>Banco<input value={form.bank} onChange={(e) => set("bank", e.target.value)} placeholder="Ej: BBVA" data-testid="emp-bank" /></label>
              <label>CLABE bancaria (18 dígitos)<input value={form.clabe} onChange={(e) => set("clabe", e.target.value.replace(/\D/g, ""))} maxLength={18} data-testid="emp-clabe" /></label>
              <label>Número de cuenta<input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} data-testid="emp-account" /></label>
              <p style={{ background: "#dcf7ed", padding: 14, borderRadius: 12, color: "#238a65", fontSize: 13, marginTop: 12 }}>
                <CheckCircle2 size={15} /> Listo. Guardaremos a este empleado y podrás correr su primera nómina.
              </p>
            </>
          )}

          {error && <p className="expenses-error" data-testid="emp-error" style={{ marginTop: 12 }}>{error}</p>}
        </section>

        <div style={{ display: "flex", gap: 10, marginTop: 20, maxWidth: 720 }}>
          {step > 1 && <button type="button" onClick={() => setStep(step - 1)} className="cash-btn ghost" data-testid="emp-back">Atrás</button>}
          {step < 4 ? (
            <button
              type="button"
              onClick={() => canNext && setStep(step + 1)}
              className="cash-btn primary"
              disabled={!canNext}
              data-testid="emp-next"
              style={{ marginLeft: "auto" }}
            >
              Siguiente <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={saving || !form.first_name || !form.last_name || Number(form.gross_salary) <= 0}
              className="cash-btn primary"
              data-testid="emp-submit"
              style={{ marginLeft: "auto" }}
            >
              <Users2 size={15} /> {saving ? "Guardando…" : "Guardar empleado"}
            </button>
          )}
        </div>
      </main>
    </AppShell>
  );
}
